import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, {
  SyntheticEvent,
  memo,
  useCallback,
  useRef,
  useState,
} from 'react';
import type { Unit } from '../../types/Store.types';
import AutocompleteField, {
  AutocompleteOption,
  AutocompleteProps,
} from '../AutocompleteField';
import { Item } from './UnitAutocompleteField.styles';

export type UnitAutocompleteFieldProps = {
  buildingId?: string;
  onChange: (value: Unit) => void;
  onUnitAddClick: (inputText: string) => void;
  options: Option[];
  isLoading: boolean;
} & Omit<AutocompleteProps<AutocompleteOption>, 'options' | 'onChange'>;

type Option = AutocompleteOption;

const emptyArray: Option[] = [];

function UnitAutocompleteSelectField({
  value,
  testId,
  disabled,
  isLoading,
  onChange,
  onUnitAddClick,
  onInputChange,
  options = emptyArray,
  ...props
}: UnitAutocompleteFieldProps) {
  const inputValueRef = useRef('');
  const [inputValue, setInputValue] = useState('');

  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = async () => {
    if (disabled) {
      return;
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleInputChange = useCallback(
    async (event: SyntheticEvent | null, value: string) => {
      setInputValue(value);
      inputValueRef.current = value;

      if (onInputChange) {
        onInputChange(event, value);
      }
    },
    [onInputChange],
  );

  const handleChange = (
    event: SyntheticEvent,
    selectedValue: Option | null,
  ) => {
    if (selectedValue) {
      onChange({
        id: (selectedValue.id as string) || '',
        name: selectedValue.name,
      });
    }

    if (!selectedValue) {
      onChange({
        id: '',
        name: '',
      });
    }
  };

  const val = inputValue || value?.name || '';

  const filterOptions = (options: Option[]) => {
    return options.filter((option) =>
      option.name?.toLowerCase().includes(inputValueRef.current?.toLowerCase()),
    );
  };

  const dontSeeUnitJsx = (
    <Item onClick={() => onUnitAddClick(val)}>
      <Typography variant="subtitle1">
        Don't see your unit?&nbsp;
        <span
          style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}
        >
          Click to add your unit
        </span>
      </Typography>
    </Item>
  );

  return (
    <AutocompleteField
      data-testid={testId}
      options={options}
      filterOptions={filterOptions}
      loading={isLoading}
      open={isOpen}
      isOptionEqualToValue={(option: Option, value: Option) =>
        option.name === value.name
      }
      disabled={disabled}
      onOpen={handleOpen}
      onClose={handleClose}
      onInputChange={handleInputChange}
      onChange={handleChange}
      value={value}
      renderOption={(props, option) => (
        <Item {...props} key={option.name}>
          <Typography variant="subtitle1">{option.name}</Typography>
        </Item>
      )}
      ListboxComponent={({ children, ...props }) => (
        <Box component="ul" sx={{ maxHeight: '175px !important' }} {...props}>
          {children}
          {dontSeeUnitJsx}
        </Box>
      )}
      noOptionsText={
        <Box component="ul" sx={{ p: 0 }}>
          {dontSeeUnitJsx}
        </Box>
      }
      {...props}
    />
  );
}

export default memo(UnitAutocompleteSelectField);
